import { Link } from "@/lib/router-compat";
import { Flame, TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type { LiveToken } from "@/hooks/useLiveMarkets";
import { cdnImage } from "@/lib/images";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

interface Trending {
  id: string;
  symbol: string;
  name: string;
  thumb: string;
  price?: number;
  change24h?: number;
}

const fmtPrice = (p?: number | null) => {
  if (p == null) return "-";
  if (p >= 1) return `$${p.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${p.toLocaleString(undefined, { maximumFractionDigits: 6 })}`;
};

const fmtPct = (p?: number | null) => {
  if (p == null) return null;
  const cls = p >= 0 ? "text-success" : "text-destructive";
  return <span className={cn("text-xs tabular-nums", cls)}>{p >= 0 ? "+" : ""}{p.toFixed(2)}%</span>;
};

interface RowProps {
  rank: number;
  id: string;
  symbol: string;
  name: string;
  image?: string;
  price?: number | null;
  change?: number | null;
}
const Row = ({ rank, id, symbol, name, image, price, change }: RowProps) => (
  <Link
    to={`/markets/${id}`}
    className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-md hover:bg-muted/50 transition-colors"
  >
    <span className="text-xs text-muted-foreground w-4 tabular-nums">{rank}</span>
    {image && <img src={cdnImage(image, { width: 20, height: 20, fit: "contain" })} alt="" className="h-5 w-5 rounded-full" loading="lazy" />}
    <span className="text-sm font-medium truncate flex-1">{name}</span>
    <span className="text-xs text-muted-foreground uppercase">{symbol}</span>
    <span className="text-sm font-medium tabular-nums w-20 text-right">{fmtPrice(price)}</span>
    <span className="w-16 text-right">{fmtPct(change)}</span>
  </Link>
);

interface CardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}
const Card = ({ title, icon, children }: CardProps) => (
  <div className="rounded-xl border border-border/60 bg-card p-4">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

interface Props {
  tokens: LiveToken[];
}

const HighlightsBento = ({ tokens }: Props) => {
  const t = useT();
  const [trending, setTrending] = useState<Trending[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch("https://api.coingecko.com/api/v3/search/trending")
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        const items: Trending[] = (j?.coins || []).slice(0, 5).map((c: any) => ({
          id: c.item.id,
          symbol: c.item.symbol,
          name: c.item.name,
          thumb: c.item.thumb,
          price: c.item.data?.price ? Number(c.item.data.price) : undefined,
          change24h: c.item.data?.price_change_percentage_24h?.usd,
        }));
        setTrending(items);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const gainers = useMemo(
    () =>
      [...tokens]
        .filter(t => (t.price_change_percentage_24h_in_currency ?? 0) > 0)
        .sort((a, b) => (b.price_change_percentage_24h_in_currency ?? 0) - (a.price_change_percentage_24h_in_currency ?? 0))
        .slice(0, 5),
    [tokens],
  );

  const losers = useMemo(
    () =>
      [...tokens]
        .filter(t => (t.price_change_percentage_24h_in_currency ?? 0) < 0)
        .sort((a, b) => (a.price_change_percentage_24h_in_currency ?? 0) - (b.price_change_percentage_24h_in_currency ?? 0))
        .slice(0, 5),
    [tokens],
  );

  // CoinGecko market response has no "added" timestamp; use highest rank (lowest mcap) as proxy for "Recently added"
  const recent = useMemo(
    () =>
      [...tokens]
        .filter(t => t.market_cap_rank)
        .sort((a, b) => (b.market_cap_rank ?? 0) - (a.market_cap_rank ?? 0))
        .slice(0, 5),
    [tokens],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 my-6">
      <Card title={t("auto.trending-3752")} icon={<Flame className="h-4 w-4 text-orange-500" />}>
        {trending.length === 0 && <p className="text-xs text-muted-foreground py-2"><T k="auto.loading-33ce" /></p>}
        {trending.map((t, i) => (
          <Row key={t.id} rank={i + 1} id={t.id} symbol={t.symbol} name={t.name} image={t.thumb} price={t.price} change={t.change24h} />
        ))}
      </Card>
      <Card title={t("auto.top-gainers-0413")} icon={<TrendingUp className="h-4 w-4 text-success" />}>
        {gainers.map((t, i) => (
          <Row key={t.id} rank={i + 1} id={t.id} symbol={t.symbol} name={t.name} image={t.image} price={t.current_price} change={t.price_change_percentage_24h_in_currency} />
        ))}
      </Card>
      <Card title={t("auto.top-losers-d4cc")} icon={<TrendingDown className="h-4 w-4 text-destructive" />}>
        {losers.map((t, i) => (
          <Row key={t.id} rank={i + 1} id={t.id} symbol={t.symbol} name={t.name} image={t.image} price={t.current_price} change={t.price_change_percentage_24h_in_currency} />
        ))}
      </Card>
      <Card title={t("auto.recently-added-e1a4")} icon={<Sparkles className="h-4 w-4 text-primary" />}>
        {recent.map((t, i) => (
          <Row key={t.id} rank={i + 1} id={t.id} symbol={t.symbol} name={t.name} image={t.image} price={t.current_price} change={t.price_change_percentage_24h_in_currency} />
        ))}
      </Card>
    </div>
  );
};

export default HighlightsBento;
